const DEFAULT_SHEETS_DOMAIN = 'parrotsheets.com';
const DEFAULT_SHEETS_DOMAINS = new Set([
  DEFAULT_SHEETS_DOMAIN,
  `www.${DEFAULT_SHEETS_DOMAIN}`
]);

const PRIMARY_APP_DOMAINS = new Set([
  'parrotdoc.co.za',
  'www.parrotdoc.co.za',
  'parrotdocs.co.za',
  'www.parrotdocs.co.za',
  'parrotdocs.com',
  'www.parrotdocs.com',
  'parrotdocs.web.app',
  'parrotdocs.firebaseapp.com'
]);

const LOCAL_HOSTS = new Set([
  'localhost',
  '127.0.0.1'
]);

export const normalizeDomainHost = (value = '') => `${value || ''}`
  .trim()
  .toLowerCase()
  .replace(/^https?:\/\//, '')
  .replace(/\/.*$/, '')
  .replace(/:\d+$/, '');

export const normalizeWorkspaceCustomDomain = (value = '') => {
  const normalized = normalizeDomainHost(value);
  if (!normalized) return '';
  return normalized.replace(/[^a-z0-9.-]/g, '');
};

export const isLocalHost = (hostname = window.location.hostname) => {
  const normalized = normalizeDomainHost(hostname);
  return LOCAL_HOSTS.has(normalized) || normalized.endsWith('.local');
};

export const isDefaultSheetsHost = (hostname = window.location.hostname) => (
  DEFAULT_SHEETS_DOMAINS.has(normalizeDomainHost(hostname))
);

export const getDefaultSheetsOrigin = () => `https://${DEFAULT_SHEETS_DOMAIN}`;

export const getWorkspacePath = (workspaceSlug = '') => {
  const slug = `${workspaceSlug || ''}`.trim();
  if (!slug) return '/sheets';
  return isDefaultSheetsHost() ? `/workspace/${slug}` : `/sheets/workspace/${slug}`;
};

export const getWorkspacesPath = () => (isDefaultSheetsHost() ? '/workspaces' : '/sheets/workspaces');

export const getWorkspaceCreatePath = () => (isDefaultSheetsHost() ? '/workspaces/new' : '/sheets/workspaces/new');

export const getWorkspaceEditPath = (workspaceSlug = '') => {
  const slug = `${workspaceSlug || ''}`.trim();
  if (!slug) return getWorkspacesPath();
  return isDefaultSheetsHost() ? `/workspaces/${slug}/edit` : `/sheets/workspaces/${slug}/edit`;
};

export const getWorkspaceNewSheetPath = (workspaceSlug = '') => {
  const slug = `${workspaceSlug || ''}`.trim();
  if (!slug) return isDefaultSheetsHost() ? '/new' : '/sheets/new';
  return isDefaultSheetsHost() ? `/workspace/${slug}/new` : `/sheets/workspace/${slug}/new`;
};

export const getSheetsLibraryPath = () => (isDefaultSheetsHost() ? '/' : '/sheets');
export const getSheetsLibraryUrl = () => getDefaultSheetsOrigin();

export const getSheetPath = (sheetId = '', workspaceOrSlug = null) => {
  const id = `${sheetId || ''}`.trim();
  if (!id) return '/sheets';

  const workspaceSlug = `${typeof workspaceOrSlug === 'string'
    ? workspaceOrSlug
    : workspaceOrSlug?.slug || ''}`.trim();

  if (!workspaceSlug) return `/sheet/${id}`;
  if (isLikelyWorkspaceCustomHost()) return `/sheet/${id}`;
  return isDefaultSheetsHost()
    ? `/workspace/${workspaceSlug}/sheet/${id}`
    : `/sheets/workspace/${workspaceSlug}/sheet/${id}`;
};

export const getWorkspaceUrl = (workspace = {}) => {
  const customDomain = normalizeWorkspaceCustomDomain(workspace?.customDomain);
  if (customDomain) return `https://${customDomain}`;

  const slug = `${workspace?.slug || ''}`.trim();
  if (!slug) return getDefaultSheetsOrigin();
  return `${getDefaultSheetsOrigin()}/workspace/${slug}`;
};

export const getSheetUrl = (sheetId = '', workspaceOrSlug = null) => {
  const id = `${sheetId || ''}`.trim();
  const workspaceSlug = `${typeof workspaceOrSlug === 'string'
    ? workspaceOrSlug
    : workspaceOrSlug?.slug || ''}`.trim();
  const customDomain = normalizeWorkspaceCustomDomain(
    typeof workspaceOrSlug === 'string' ? '' : workspaceOrSlug?.customDomain
  );

  if (customDomain) return `https://${customDomain}/sheet/${id}`;
  return `${getDefaultSheetsOrigin()}${getSheetPath(id, workspaceSlug)}`;
};

export const isLikelyWorkspaceCustomHost = (hostname = window.location.hostname) => {
  const normalized = normalizeDomainHost(hostname);
  return Boolean(normalized)
    && !isLocalHost(normalized)
    && !isDefaultSheetsHost(normalized)
    && !PRIMARY_APP_DOMAINS.has(normalized);
};
